import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Alert,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Pause,
  PlayArrow,
  Cancel,
  Download,
  Refresh,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import oneDriveService from '../services/oneDriveService';
import { extractErrorMessage } from '../../../utils/errorUtils';

const BulkImportProgress = ({ jobId, onClose }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reconnecting, setReconnecting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const pollingInterval = useRef(null);
  const reconnectAttempts = useRef(0);

  useEffect(() => {
    if (jobId) {
      loadJobStatus();
      startPolling();
    }

    return () => {
      stopPolling();
    };
  }, [jobId]);

  const loadJobStatus = async () => {
    try {
      setLoading(true);
      setReconnecting(false);
      reconnectAttempts.current = 0;
      
      const response = await oneDriveService.getJobStatus(jobId);
      setJob(response.data);
      setError('');
    } catch (err) {
      console.error('Error loading job status:', err);
      setError('Error al cargar estado del trabajo');
      handleReconnect();
    } finally {
      setLoading(false);
    }
  };

  const handleReconnect = () => {
    if (reconnectAttempts.current < 10) {
      setReconnecting(true);
      reconnectAttempts.current += 1;
      
      setTimeout(() => {
        loadJobStatus();
      }, 10000); // Reintentar cada 10 segundos
    }
  };

  const startPolling = () => {
    pollingInterval.current = setInterval(async () => {
      try {
        const response = await oneDriveService.getJobStatus(jobId);
        setJob(response.data);
        
        // Detener polling si el trabajo terminó
        if (['completed', 'failed', 'cancelled'].includes(response.data.status)) {
          stopPolling();
        }
      } catch (err) {
        console.error('Polling error:', err);
        handleReconnect();
      }
    }, 5000); // Polling cada 5 segundos
  };

  const stopPolling = () => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
  };

  const handlePause = async () => {
    try {
      // Nota: La funcionalidad de pausa requiere implementación adicional en el worker
      setError('Funcionalidad de pausa no disponible aún');
    } catch (err) {
      setError('Error al pausar trabajo');
    }
  };

  const handleResume = async () => {
    try {
      await oneDriveService.resumeJob(jobId);
      loadJobStatus();
      startPolling();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleCancel = async () => {
    try {
      await oneDriveService.cancelJob(jobId);
      loadJobStatus();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleDownloadReport = async () => {
    try {
      const response = await oneDriveService.downloadReport(jobId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte-importacion-${jobId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Error al descargar reporte');
    }
  };

  if (loading && !job) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Cargando estado del trabajo...</Typography>
      </Box>
    );
  }

  if (!job) {
    return (
      <Alert severity="error">
        No se pudo cargar la información del trabajo
      </Alert>
    );
  }

  const progress = job.totalFiles > 0 ? (job.processedFiles / job.totalFiles) * 100 : 0;
  const isActive = ['pending', 'processing'].includes(job.status);
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed';
  const isPaused = job.status === 'paused';

  const getStatusColor = () => {
    if (isCompleted) return 'success';
    if (isFailed) return 'error';
    if (isPaused) return 'warning';
    return 'primary';
  };

  const getStatusText = () => {
    if (reconnecting) return 'Reconectando...';
    switch (job.status) {
      case 'pending': return 'Pendiente';
      case 'processing': return 'Procesando';
      case 'paused': return 'Pausado';
      case 'completed': return 'Completado';
      case 'failed': return 'Fallido';
      case 'cancelled': return 'Cancelado';
      default: return job.status;
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {reconnecting && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Conexión perdida. Reintentando... (Intento {reconnectAttempts.current}/10)
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Estado de Importación
          </Typography>
          <Chip
            label={getStatusText()}
            color={getStatusColor()}
            icon={
              isCompleted ? <CheckCircle /> :
              isFailed ? <ErrorIcon /> :
              reconnecting ? <Refresh /> : undefined
            }
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progreso
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {job.processedFiles} / {job.totalFiles} archivos ({progress.toFixed(1)}%)
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Exitosos
            </Typography>
            <Typography variant="h6" color="success.main">
              {job.successCount}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Fallidos
            </Typography>
            <Typography variant="h6" color="error.main">
              {job.failedCount}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Reintentos
            </Typography>
            <Typography variant="h6">
              {job.retryCount}
            </Typography>
          </Box>
        </Box>

        {job.lastError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Último error: {job.lastError}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {isActive && (
            <>
              <Button
                size="small"
                startIcon={<Pause />}
                onClick={handlePause}
                disabled
              >
                Pausar
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<Cancel />}
                onClick={handleCancel}
              >
                Cancelar
              </Button>
            </>
          )}

          {isPaused && (
            <Button
              size="small"
              color="primary"
              startIcon={<PlayArrow />}
              onClick={handleResume}
            >
              Reanudar
            </Button>
          )}

          {isCompleted && job.reportPath && (
            <Button
              size="small"
              color="primary"
              startIcon={<Download />}
              onClick={handleDownloadReport}
            >
              Descargar Reporte
            </Button>
          )}

          <Button
            size="small"
            startIcon={<Refresh />}
            onClick={loadJobStatus}
          >
            Actualizar
          </Button>
        </Box>
      </Paper>

      {job.errorLog && Array.isArray(job.errorLog) && job.errorLog.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setShowErrors(!showErrors)}
          >
            <Typography variant="subtitle2">
              Errores ({job.errorLog.length})
            </Typography>
            <IconButton size="small">
              {showErrors ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          
          <Collapse in={showErrors}>
            <List dense sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
              {job.errorLog.slice(0, 20).map((error, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={error.fileName || 'Archivo desconocido'}
                    secondary={error.error || error.message}
                  />
                </ListItem>
              ))}
              {job.errorLog.length > 20 && (
                <ListItem>
                  <ListItemText
                    secondary={`... y ${job.errorLog.length - 20} errores más. Descarga el reporte para ver todos.`}
                  />
                </ListItem>
              )}
            </List>
          </Collapse>
        </Paper>
      )}

      {isCompleted && job.metadata?.fileResults && job.metadata.fileResults.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setShowResults(!showResults)}
          >
            <Typography variant="subtitle2">
              Resultados por archivo ({job.metadata.fileResults.length})
            </Typography>
            <IconButton size="small">
              {showResults ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={showResults}>
            <TableContainer sx={{ maxHeight: 320, mt: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Archivo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Expediente</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {job.metadata.fileResults.slice(0, 100).map((row, idx) => (
                    <TableRow key={idx} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.fileName || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.proceedingName || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.status === 'Exitoso' ? '✓' : '✗'}
                          size="small"
                          color={row.status === 'Exitoso' ? 'success' : 'error'}
                          sx={{ minWidth: 36, fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell>
                        {row.proceedingUrl && (
                          <Button
                            size="small"
                            variant="text"
                            href={row.proceedingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ minWidth: 0, p: '2px 6px', fontSize: '11px' }}
                          >
                            Ver expediente
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {job.metadata.fileResults.length > 100 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', fontSize: 12 }}>
                        ... y {job.metadata.fileResults.length - 100} más. Descarga el reporte Excel para ver todos.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Paper>
      )}

      {isCompleted && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={onClose}>
            Cerrar
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BulkImportProgress;
